/* BulkGLP1R rewards: reads the saved cart; never changes prices or cart contents. */
(() => {
  'use strict';
  // Shopify renders the current variants for this store's four eligible products.
  const runtimeConfig = (() => { try { return typeof document === 'undefined' ? {} : JSON.parse(document.querySelector('[data-rewards-config]')?.textContent || '{}'); } catch (_) { return {}; } })();
  const ELIGIBLE_IDS = Object.freeze(Array.isArray(runtimeConfig.eligibleVariantIds) ? runtimeConfig.eligibleVariantIds.map(String) : []);
  const PACK_IDS = Object.freeze([]);
  const TIERS = Object.freeze([{ quantity: 2, percent: 5 }, { quantity: 3, percent: 10 }, { quantity: 5, percent: 15 }, { quantity: 10, percent: 20 }]);
  const MILESTONES = Object.freeze([{ cents: 9900, key: 'shipping', basis: 'before' }, { cents: 35000, key: 'gift', basis: 'after' }, { cents: 50000, key: 'express', basis: 'before' }]);
  const eligibleIds = new Set(ELIGIBLE_IDS);
  const isEligible = (id) => eligibleIds.has(String(id));
  const whole = (value) => Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : 0;
  const eligibleQuantity = (cart) => (cart?.items || []).reduce((total, item) => total + (isEligible(item.variant_id ?? item.id) ? whole(item.quantity) : 0), 0);
  const tierFor = (quantity) => TIERS.reduce((tier, next) => whole(quantity) >= next.quantity ? next : tier, { quantity: 0, percent: 0 });
  const nextTier = (quantity) => TIERS.find((tier) => tier.quantity > whole(quantity)) || null;
  const rewardState = (savedCart, { giftEnabled = false, shippingEnabled = false } = {}) => {
    const total = whole(savedCart?.total_price);
    const shippingTotal = whole(savedCart?.original_total_price);
    const milestones = MILESTONES.filter((m) => m.key === 'gift' ? giftEnabled : shippingEnabled).map((m) => ({ ...m, remaining: Math.max(0, m.cents - (m.basis === 'before' ? shippingTotal : total)) }));
    // The single progress track is explicitly SHIPPING ONLY; gift eligibility uses its own net baseline.
    return { total, shippingTotal, milestones, unlocked: milestones.filter((m) => m.remaining === 0).map((m) => m.key), next: milestones.find((m) => m.remaining > 0) || null, progress: Math.min(shippingTotal, 50000) };
  };
  const estimate = (price, quantity, savedEligible, variantId) => {
    if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(price * quantity)) return null;
    const eligible = isEligible(variantId);
    const combined = whole(savedEligible) + (eligible ? quantity : 0);
    const percent = eligible ? tierFor(combined).percent : 0;
    // Integer minor units. The displayed estimate is not a Shopify discount allocation.
    const lineCents = Math.round(price * quantity * (100 - percent) / 100);
    return { eligible, combined, percent, lineCents, unitCents: Math.round(lineCents / quantity), next: nextTier(combined) };
  };
  const validCart = (cart) => Boolean(cart && Array.isArray(cart.items) && Number.isSafeInteger(cart.total_price) && cart.total_price >= 0 && Number.isSafeInteger(cart.original_total_price) && cart.original_total_price >= 0);
  // Reprice every eligible saved line at the proposed combined tier. Never add a discounted
  // selection to an already-discounted subtotal: the saved items can reach a better tier too.
  const previewCart = (savedCart, selection) => {
    const price = selection?.price;
    const quantity = selection?.quantity;
    const variantId = selection?.variantId;
    const cents = (value) => Number.isSafeInteger(value) && value >= 0;
    if (!validCart(savedCart) || !isEligible(variantId) || !cents(price) || !Number.isSafeInteger(quantity) || quantity < 1 || !cents(price * quantity)) return null;
    if (selection.currency && savedCart.currency && selection.currency !== savedCart.currency) return null;
    let original = 0, final = 0, savedEligible = 0, unchangedNet = 0;
    const eligibleLines = [];
    for (const item of savedCart.items) {
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1 || !cents(item.original_line_price) || !cents(item.final_line_price)) return null;
      original += item.original_line_price;
      final += item.final_line_price;
      if (isEligible(item.variant_id ?? item.id)) { savedEligible += item.quantity; eligibleLines.push(item.original_line_price); }
      else unchangedNet += item.final_line_price;
    }
    // Missing prices or an unmodelled order-level adjustment must not produce a reward claim.
    if (!cents(original) || !cents(final) || original !== savedCart.original_total_price || final !== savedCart.total_price || !Number.isSafeInteger(savedEligible + quantity)) return null;
    const combined = savedEligible + quantity;
    const percent = tierFor(combined).percent;
    const lineOriginal = price * quantity;
    const lineCents = Math.round(lineOriginal * (100 - percent) / 100);
    const total = unchangedNet + lineCents + eligibleLines.reduce((sum, amount) => sum + Math.round(amount * (100 - percent) / 100), 0);
    const gross = original + lineOriginal;
    if (!cents(total) || !cents(gross)) return null;
    return { eligible: true, combined, savedEligible, quantity, percent, lineCents, unitCents: Math.round(lineCents / quantity), next: nextTier(combined), original_total_price: gross, total_price: total };
  };
  const createSelectionPreview = () => {
    let formId = null, suppressed = false;
    return {
      get: () => ({ formId, suppressed }),
      select: (id) => { formId = id; suppressed = false; },
      clear: () => { formId = null; suppressed = true; }
    };
  };
  // Mutation events outrank outstanding reads. A pending boundary invalidates every older GET,
  // and a monotonic mutation revision prevents duplicate/out-of-order events rolling totals back.
  const createCartCoordinator = (initialCart = null) => {
    let cart = validCart(initialCart) ? initialCart : null;
    let revision = -1;
    let epoch = 0;
    let pending = false;
    let error = '';
    let enhanced = false;
    return {
      get: () => ({ cart, revision, epoch, pending, error, enhanced }),
      seed: (candidate) => { if (!cart && validCart(candidate)) cart = candidate; },
      beginRead: () => ({ epoch, revision }),
      acceptRead: (candidate, ticket) => {
        if (!validCart(candidate) || pending || ticket.epoch !== epoch || ticket.revision !== revision) return false;
        cart = candidate; epoch += 1; return true;
      },
      acceptMutation: (candidate, nextRevision) => {
        if (!validCart(candidate) || !Number.isSafeInteger(nextRevision) || nextRevision < 0 || nextRevision <= revision) return false;
        cart = candidate; revision = nextRevision; epoch += 1; enhanced = true; error = ''; return true;
      },
      setPending: (detail) => {
        pending = detail?.pending === true;
        error = typeof detail?.error === 'string' ? detail.error : '';
        enhanced = true; epoch += 1;
      }
    };
  };
  const math = { ELIGIBLE_IDS, PACK_IDS, TIERS, MILESTONES, isEligible, eligibleQuantity, tierFor, nextTier, rewardState, estimate, previewCart, createSelectionPreview, validCart, createCartCoordinator };
  if (typeof module !== 'undefined' && module.exports) module.exports = math;
  if (typeof document === 'undefined') return;
  if (document.documentElement.dataset.rewardsRuntime === 'true') return;
  document.documentElement.dataset.rewardsRuntime = 'true';
  const coordinator = createCartCoordinator();
  const selectionPreview = createSelectionPreview();
  let controller = null;
  let stale = false;
  let notificationVisible = false;
  let activeRead = null;
  let readGeneration = 0;

  function createController(bar) {
    bar.dataset.initialized = 'true';
    const lifecycle = new AbortController();
    const listen = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: lifecycle.signal });
    const readJSON = (element, fallback) => { try { return JSON.parse(element?.textContent || ''); } catch (_) { return fallback; } };
    const config = readJSON(document.querySelector('[data-rewards-config]'), {});
    coordinator.seed(config.cart);
    let cart = coordinator.get().cart || { original_total_price: 0, total_price: 0, currency: 'USD', items: [] };
    let savedCartDirty = false;
    let lastDockHeight = 130;
    let scrollFrame = 0;
    const panels = new Map();
    const details = bar.querySelector('[data-rewards-details]');
    const toggle = bar.querySelector('[data-rewards-toggle]');
    const live = bar.querySelector('[data-rewards-live]');
    const mobileQuery = window.matchMedia?.('(max-width: 749px)');
    const isMobileDock = () => !bar.hasAttribute('data-rewards-inline') && mobileQuery?.matches === true;
    const currency = () => cart.currency || config.currency || 'USD';
    const money = (cents) => {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency() }).format(cents / 100); }
      catch (_) { return '$' + (cents / 100).toFixed(2); }
    };
    const set = (selector, value) => { const node = bar.querySelector(selector); if (node && node.textContent !== value) node.textContent = value; };
    const itemWord = (count) => count === 1 ? 'item' : 'items';
    const compactMoney = (cents) => money(cents).replace(/\.00$/, '');
    const isEnhanced = () => document.documentElement.dataset.cartEnhanced === 'true' || coordinator.get().enhanced;
    const syncPending = () => coordinator.get().pending || (isEnhanced() && savedCartDirty && !coordinator.get().error);

    function panelSelection(state, panel) {
      const selectedId = state.form?.querySelector('[name="id"]')?.value || panel.dataset.selectedVariant;
      const variant = state.variants.find((v) => String(v.id) === String(selectedId));
      const quantityInput = state.form?.querySelector('[name="quantity"]');
      const raw = quantityInput?.value ?? '1';
      const quantity = /^\d+$/.test(raw) ? Number(raw) : NaN;
      const result = variant && variant.available !== false && (!quantityInput || quantityInput.validity.valid) ? previewCart(cart, { price: Number(variant.price), quantity, variantId: variant.id, currency: config.currency }) : null;
      return { variant, quantityInput, quantity, result };
    }

    function dockSelection() {
      if (bar.hasAttribute('data-rewards-inline')) return null;
      const formId = selectionPreview.get().formId;
      for (const [panel, state] of panels) {
        if (state.form?.id === formId && state.form.isConnected) return panelSelection(state, panel);
      }
      return null;
    }

    function renderPanels() {
      panels.forEach((state, panel) => {
        const { variant, quantityInput, quantity, result } = panelSelection(state, panel);
        const qualifying = variant && isEligible(variant.id);
        panel.querySelector('[data-volume-tiers]').hidden = !qualifying;
        panel.querySelectorAll('[data-volume-tier]').forEach((row) => {
          const selected = selectionPreview.get().formId === state.form?.id;
          const active = selected && !syncPending() && !coordinator.get().error && result?.eligible && Number(row.dataset.volumeTier) === tierFor(result.combined).quantity;
          row.classList.toggle('is-current', Boolean(active));
          if (active) row.setAttribute('aria-current', 'true'); else row.removeAttribute('aria-current');
        });
        const summary = panel.querySelector('[data-volume-summary]');
        const detail = panel.querySelector('[data-volume-preview]');
        if (syncPending()) {
          summary.textContent = 'Updating cart…';
          detail.textContent = '';
        } else if (coordinator.get().error) {
          summary.textContent = 'Check your cart before retrying.';
          detail.textContent = '';
        } else if (!qualifying) {
          summary.textContent = 'See your cart for available discounts.';
          detail.textContent = '';
        } else if (stale || savedCartDirty) {
          summary.textContent = savedCartDirty ? 'Update your cart to refresh savings.' : 'Savings preview unavailable.';
          detail.textContent = 'See your cart for the current price.';
        } else if (selectionPreview.get().suppressed) {
          const savedTier = tierFor(eligibleQuantity(cart));
          summary.textContent = savedTier.percent ? `${savedTier.percent}% volume savings in your cart` : 'Savings apply automatically in your cart.';
          detail.textContent = '';
        } else if (selectionPreview.get().formId !== state.form?.id) {
          summary.textContent = 'Savings apply automatically in your cart.';
          detail.textContent = '';
        } else if (!Number.isSafeInteger(quantity) || quantity < 1 || (quantityInput && !quantityInput.validity.valid)) {
          summary.textContent = 'Enter a whole quantity to preview savings.';
          detail.textContent = '';
        } else if (variant.available === false) {
          summary.textContent = 'This selection is sold out.';
          detail.textContent = '';
        } else if (!result) {
          summary.textContent = 'Savings preview unavailable.';
          detail.textContent = 'Final savings are shown in your cart.';
        } else {
          summary.textContent = result.percent ? `${result.percent}% estimated volume savings` : 'Volume savings start at 2 eligible items.';
          detail.textContent = result.percent ? `Estimated total with this selection: ${money(result.total_price)}. Final price in cart.` : '';
        }
        detail.hidden = !detail.textContent;
      });
    }

    function render(announce = false) {
      cart = coordinator.get().cart || cart;
      savedCartDirty = [...document.querySelectorAll('#CartForm [name="updates[]"], #CartForm [name="note"]')].some((input) => input.value !== input.defaultValue);
      const pending = syncPending();
      const mutationError = coordinator.get().error;
      const blocked = pending || Boolean(mutationError);
      const selection = dockSelection();
      const preview = !blocked && !stale && !savedCartDirty ? selection?.result : null;
      const previewUnavailable = !blocked && Boolean(selection) && !preview;
      const previewing = Boolean(preview);
      const previewPrefix = previewing ? 'Preview: ' : '';
      bar.dataset.rewardsPreview = previewing ? 'true' : 'false';
      bar.setAttribute('aria-busy', String(pending));
      details.hidden = minimized || blocked || previewUnavailable;
      toggle.disabled = blocked || previewUnavailable;
      toggle.setAttribute('aria-expanded', String(!minimized && !blocked && !previewUnavailable));
      toggle.setAttribute('aria-controls', isMobileDock() ? 'RewardsContent' : 'RewardsDetails');
      toggle.textContent = isMobileDock() ? minimized || blocked || previewUnavailable ? mutationError ? 'Cart needs attention' : pending ? 'Updating cart…' : 'Rewards & savings' : 'Close' : minimized || blocked || previewUnavailable ? 'Details' : 'Hide details';
      bar.classList.toggle('is-minimized', minimized || blocked || previewUnavailable);
      const quantity = previewing ? preview.combined : eligibleQuantity(cart);
      const tier = tierFor(quantity);
      const reward = rewardState(preview || cart, { giftEnabled: config.giftEnabled === true, shippingEnabled: config.shippingEnabled === true });
      const bulkText = previewUnavailable ? 'Estimate unavailable' : previewing && tier.percent ? `${tier.percent}% estimated volume savings` : tier.percent ? `${tier.percent}% volume savings` : 'Rewards & savings';
      const usd = currency() === 'USD';
      const rewardText = previewUnavailable ? 'Current benefits are shown in your cart' : previewing ? `With ${preview.quantity} selected · ${quantity} eligible ${itemWord(quantity)}` : quantity ? `${quantity} eligible ${itemWord(quantity)} in your cart` : 'Automatic volume savings';
      const currentBenefit = usd && reward.unlocked.includes('express') ? 'Free express shipping unlocked' : usd && reward.unlocked.includes('shipping') ? 'Free US shipping unlocked' : '';
      set('[data-rewards-bulk]', pending ? 'Updating cart…' : mutationError ? 'Cart needs attention' : bulkText);
      set('[data-rewards-next]', mutationError ? '' : pending ? currentBenefit : rewardText);
      bar.querySelector('[data-rewards-next]').hidden = Boolean(mutationError) || (pending && !currentBenefit);
      set('[data-rewards-subtotal]', `${previewing ? 'Preview subtotal' : 'Saved subtotal'} after discounts: ${money(reward.total)}`);
      set('[data-rewards-shipping-subtotal]', `${previewing ? 'Preview shipping subtotal' : 'Shipping subtotal'} before discounts: ${money(reward.shippingTotal)}`);
      set('[data-rewards-quantity]', previewing ? `Preview: ${preview.savedEligible} saved + ${preview.quantity} selected = ${quantity} eligible ${itemWord(quantity)}` : `${quantity} eligible ${itemWord(quantity)} saved${tier.percent ? ` · ${tier.percent}% quantity tier` : ''}`);
      const progress = bar.querySelector('[data-rewards-progress]');
      progress.hidden = config.shippingEnabled !== true || !usd || blocked || previewUnavailable;
      progress.value = reward.progress;
      progress.setAttribute('aria-valuetext', `${previewPrefix}${money(reward.shippingTotal)} before discounts toward the $500 free US express shipping threshold. This track measures shipping rewards only.`);
      bar.querySelectorAll('[data-reward-milestone]').forEach((node) => {
        const unlocked = usd && reward.unlocked.includes(node.dataset.rewardMilestone);
        node.classList.toggle('is-earned', unlocked);
        const milestone = reward.milestones.find((m) => m.key === node.dataset.rewardMilestone);
        node.querySelector('[data-reward-status]').textContent = unlocked ? previewing ? 'Would qualify' : 'Unlocked' : usd && milestone ? `${compactMoney(milestone.remaining)} to go` : 'See checkout';
      });
      const notice = mutationError ? 'Check your cart before retrying.' : pending ? '' : savedCartDirty && !isEnhanced() ? 'Select Update cart to save changes.' : stale ? 'Could not refresh rewards. Refresh the page to retry.' : '';
      set('[data-rewards-notice]', notice);
      bar.querySelector('[data-rewards-notice]').hidden = !notice;
      if (announce) {
        const sentence = pending ? 'Updating cart…' : notice || `${bulkText}. ${rewardText}.`;
        if (live.textContent !== sentence) live.textContent = sentence;
      }
      renderPanels();
      scheduleDock();
    }

    function checkPending() {
      render();
    }
    function bindPanels() {
      document.querySelectorAll('[data-volume-savings]').forEach((panel) => {
        const form = document.getElementById(panel.dataset.productFormId);
        if (panels.get(panel)?.form === form) return;
        panels.set(panel, { form, variants: readJSON(panel.querySelector('[data-volume-variants]'), []) });
      });
      panels.forEach((_, panel) => { if (!panel.isConnected) panels.delete(panel); });
    }
    function setMinimized(value) {
      minimized = value;
      render();
    }
    function updateDock() {
      scrollFrame = 0;
      // In-cart rewards remain in normal document flow; checkout/footer never overlap them.
      if (bar.hasAttribute('data-rewards-inline')) { bar.hidden = false; return; }
      const toast = document.querySelector('[data-cart-notification]');
      if (notificationVisible || (toast && !toast.hidden && toast.getClientRects().length > 0 && getComputedStyle(toast).visibility !== 'hidden')) { bar.hidden = true; return; }
      const height = bar.offsetHeight;
      if (isMobileDock() && bar.classList.contains('is-minimized')) lastDockHeight = 44;
      else if (height) lastDockHeight = height;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const viewportTop = window.visualViewport?.offsetTop || 0;
      const limit = viewportTop + viewportHeight;
      const intersects = (element, zoneTop) => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > zoneTop && rect.top < limit;
      };
      const footerVisible = [...document.querySelectorAll('.site-footer')].some((el) => intersects(el, viewportTop));
      const protectedControls = '[name="checkout"], [data-add-to-cart], [data-rewards-priority], .product-form select, .product-form .quantity-input';
      // The closed dock yields to shopping controls. An explicitly opened panel stays
      // available until Close, Escape, or an outside tap, instead of vanishing on expansion.
      const controlsCovered = (!isMobileDock() || minimized) && [...document.querySelectorAll(protectedControls)].some((el) => intersects(el, limit - lastDockHeight - 24));
      const active = document.activeElement;
      const editing = active && !bar.contains(active) && active.matches('input, textarea, select, [contenteditable="true"]') && (viewportHeight < window.innerHeight - 100 || intersects(active, limit - lastDockHeight - 24));
      const modalOpen = [...document.querySelectorAll('dialog[open], [aria-modal="true"], .mobile-menu[open]')].some((element) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
      bar.hidden = Boolean(footerVisible || controlsCovered || editing || modalOpen);
    }
    function scheduleDock() { if (!scrollFrame) scrollFrame = requestAnimationFrame(updateDock); }
    // Details are opt-in on each page; browsing or restoring an old preference never opens them.
    let minimized = true;
    listen(toggle, 'click', () => setMinimized(!minimized));
    if (mobileQuery?.addEventListener) listen(mobileQuery, 'change', () => { minimized = true; render(); });
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && !minimized) { event.preventDefault(); setMinimized(true); toggle.focus(); }
    });
    listen(document, 'pointerdown', (event) => {
      if (isMobileDock() && !minimized && !bar.contains(event.target)) setMinimized(true);
    });
    listen(window, 'scroll', scheduleDock, { passive: true });
    listen(window, 'resize', scheduleDock, { passive: true });
    listen(window.visualViewport, 'resize', scheduleDock, { passive: true });
    listen(document, 'focusin', scheduleDock);
    listen(document, 'focusout', scheduleDock);
    listen(document, 'toggle', scheduleDock, { capture: true });
    ['input', 'change'].forEach((name) => listen(document, name, (event) => {
      if (event.target.closest?.('#CartForm')) checkPending();
      else if (event.target.matches?.('[name="quantity"], [name="id"]')) {
        const form = event.target.closest('.product-form');
        if (form && [...panels.values()].some((state) => state.form === form)) { selectionPreview.select(form.id); render(true); }
      }
    }));
    const resizeObserver = window.ResizeObserver ? new ResizeObserver(scheduleDock) : null;
    resizeObserver?.observe(bar);
    bindPanels();
    render();
    return {
      bar, config,
      sync(announce = false) { bindPanels(); render(announce); },
      destroy() {
        lifecycle.abort(); resizeObserver?.disconnect();
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        panels.clear(); delete bar.dataset.initialized;
      }
    };
  }

  function mount() {
    const nextBar = document.querySelector('[data-rewards-bar]');
    if (controller?.bar === nextBar && nextBar?.isConnected) return controller;
    controller?.destroy();
    controller = nextBar ? createController(nextBar) : null;
    return controller;
  }
  function cancelRead() {
    readGeneration += 1;
    activeRead?.abort(); activeRead = null;
  }
  async function refresh() {
    if (document.visibilityState === 'hidden' || coordinator.get().pending) return;
    const current = mount();
    if (!current) return;
    cancelRead();
    const generation = readGeneration;
    const ticket = coordinator.beginRead();
    const request = new AbortController();
    activeRead = request;
    try {
      const response = await fetch(current.config.cartUrl || `${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: request.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const fresh = await response.json();
      if (generation !== readGeneration || request.signal.aborted) return;
      if (!validCart(fresh)) throw new Error('Invalid cart');
      if (coordinator.acceptRead(fresh, ticket)) { stale = false; mount()?.sync(true); }
    } catch (error) {
      if (generation !== readGeneration || request.signal.aborted || error.name === 'AbortError') return;
      stale = true; mount()?.sync(true);
    } finally { if (activeRead === request) activeRead = null; }
  }
  function start() {
    // Manager listeners are installed once per page. Per-bar listeners/observers are aborted
    // when root replaces the section, preserving cart/revision/pending state outside its DOM.
    document.addEventListener('bulkpeptides:cart-changed', (event) => {
      if (coordinator.acceptMutation(event.detail?.cart, event.detail?.revision)) {
        cancelRead(); stale = false; mount()?.sync(true);
      } else if (!event.detail?.cart) refresh(); // Compatibility with older signal-only integrations.
    });
    document.addEventListener('bulkpeptides:cart-pending', (event) => {
      coordinator.setPending(event.detail); cancelRead(); mount()?.sync(true);
    });
    document.addEventListener('bulkpeptides:cart-rendered', () => { mount()?.sync(); });
    document.addEventListener('bulkpeptides:item-added', () => {
      selectionPreview.clear(); mount()?.sync();
    });
    document.addEventListener('bulkpeptides:notification-visibility', (event) => {
      notificationVisible = event.detail?.visible === true; mount()?.sync();
    });
    ['cart:updated', 'cart:change', 'cart:refresh'].forEach((name) => document.addEventListener(name, refresh));
    document.addEventListener('shopify:section:load', () => { mount()?.sync(); if (!isEnhancedPage()) refresh(); });
    // The enhanced cart manager owns read-only refreshes; do not duplicate its focus/bfcache GET.
    const backgroundRefresh = () => { mount()?.sync(); if (!isEnhancedPage()) refresh(); };
    window.addEventListener('pageshow', backgroundRefresh);
    window.addEventListener('focus', backgroundRefresh);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') backgroundRefresh(); });
    mount(); refresh();
  }
  const isEnhancedPage = () => document.documentElement.dataset.cartEnhanced === 'true' || coordinator.get().enhanced;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();

